export const SIGNAL_VARIATIONS = {
  time_of_day: [
    "12 pm",
    "8 am",
    "9 pm",
    "10 am",
    "10 pm",
    "11 am",
    "11 pm",
    "1 pm",
  ],
  browser_version: [
    "Chrome 128.0",
    "Firefox 115.2",
    "Safari 17.1",
    "Edge 119.0",
    "Opera 105.0",
    "Brave 1.67",
    "Chrome 127.3",
    "Firefox 120.0",
  ],
  zip_code: [
    "95814",
    "80202",
    "78701",
    "32301",
    "30303",
    "02108",
    "48933",
    "98501",
  ],
  device_brand: [
    "Apple iPhone",
    "Samsung Galaxy",
    "Google Pixel",
    "OnePlus",
    "Xiaomi Redmi",
    "Motorola Edge",
    "Huawei P Series",
    "Nokia XR",
  ],
  device_os: [
    "iOS 17",
    "Android 14",
    "Windows 11",
    "macOS Sonoma",
    "Linux Ubuntu",
    "ChromeOS 125",
    "iPadOS 17",
    "tvOS 17",
  ],
  carrier: [
    "Verizon",
    "AT&T",
    "T-Mobile",
    "Sprint",
    "Vodafone",
    "Claro",
    "Orange",
    "Telstra",
  ],
  city: [
    "Sacramento",
    "Denver",
    "Austin",
    "Tallahassee",
    "Atlanta",
    "Boston",
    "Lansing",
    "Olympia",
  ],
  state: ["CA", "CO", "TX", "FL", "GA", "MA", "MI", "WA"],
  page_url: [
    "cnn.com",
    "usatoday.com",
    "apnews.com",
    "cbsnews.com",
    "nypost.com",
    "msnbc.com",
    "foxnews.com",
  ],
  latitude_longitude: [
    "38.582°N, 121.494°W",
    "39.739°N, 105.990°W",
    "30.267°N, 97.743°W",
    "30.438°N, 84.281°W",
    "33.749°N, 84.388°W",
    "42.360°N, 71.059°W",
    "42.733°N, 84.556°W",
    "47.038°N, 122.901°W",
  ],
  location: [
    "San Francisco, CA",
    "Denver, CO",
    "Austin, TX",
    "Orlando, FL",
    "Atlanta, GA",
    "Boston, MA",
    "Seattle, WA",
    "Chicago, IL",
  ],
}

export const STATE_TO_CITY_CAPITALS = {
  CA: "Sacramento",
  CO: "Denver",
  TX: "Austin",
  FL: "Tallahassee",
  GA: "Atlanta",
  MA: "Boston",
  MI: "Lansing",
  WA: "Olympia",
}

export const STATE_TO_ZIP_CODES = {
  CA: "95814",
  CO: "80202",
  TX: "78701",
  FL: "32301",
  GA: "30303",
  MA: "02108",
  MI: "48933",
  WA: "98501",
}

export const DEFAULT_VARIATIONS = ["", "", "", ""]

export const ANIMATION_SETTINGS = {
  stepInterval: 150,
  slotDuration: 260,
  baseStopDelay: 1200,
  stopStagger: 260,
  cycleRest: 2000,
}

export const normalizeSignalTitle = title =>
  title
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")

// Signal key resolution patterns - order matters (more specific patterns first)
const SIGNAL_PATTERNS = [
  {
    key: "time_of_day",
    matchers: [text => text.includes("time") && text.includes("day")],
  },
  {
    key: "latitude_longitude",
    matchers: [
      text =>
        text.includes("latitude") &&
        (text.includes("longitude") || text.includes("long")),
    ],
  },
  {
    key: "device_brand",
    matchers: [text => text.includes("device") && text.includes("brand")],
  },
  {
    key: "page_url",
    matchers: [text => text.includes("page") && text.includes("url")],
  },
  {
    key: "browser_version",
    matchers: [text => text.includes("browser") || text.includes("version")],
  },
  {
    key: "device_os",
    matchers: [text => text.includes("device") || text.includes("os")],
  },
  {
    key: "zip_code",
    matchers: [text => text.includes("zip")],
  },
  {
    key: "carrier",
    matchers: [text => text.includes("carrier")],
  },
  {
    key: "city",
    matchers: [text => text.includes("city")],
  },
  {
    key: "state",
    matchers: [text => text.includes("state")],
  },
  {
    key: "location",
    matchers: [text => text.includes("location")],
  },
]

export const resolveSignalKey = title => {
  if (!title) return null

  const normalized = normalizeSignalTitle(title)

  for (const { key, matchers } of SIGNAL_PATTERNS) {
    if (matchers.some(matcher => matcher(normalized))) {
      return key
    }
  }

  return null
}

export const getVariationsForSignal = title => {
  const key = resolveSignalKey(title || "")

  if (key && SIGNAL_VARIATIONS[key]?.length) {
    return SIGNAL_VARIATIONS[key]
  }
}

export const pickRandomValue = (options, exclude) => {
  if (!options || options.length === 0) {
    return exclude ?? null
  }

  // Filter out the excluded value if provided
  const availableOptions =
    exclude != null ? options.filter(value => value !== exclude) : options

  // If no options available after filtering, return exclude or first option
  if (availableOptions.length === 0) {
    return exclude ?? options[0] ?? null
  }

  // If only one option available, return it
  if (availableOptions.length === 1) {
    return availableOptions[0]
  }

  // Pick random value from available options
  const randomIndex = Math.floor(Math.random() * availableOptions.length)
  return availableOptions[randomIndex]
}

export const getOptimalSignalColumns = signalCount => {
  if (signalCount <= 2) return 2
  if (signalCount <= 4) return 2
  if (signalCount <= 6) return 3
  if (signalCount <= 8) return 4
  return Math.ceil(Math.sqrt(signalCount))
}
